import type { CalendarDate, CalendarDateTime } from '@internationalized/date';
import { CalendarDate as CalendarDateClass, CalendarDateTime as CalendarDateTimeClass } from '@internationalized/date';
import { toZonedTime } from 'date-fns-tz';
import type { AvailabilityWindow } from '../../../api/my-parking-spots';
import {
  addMinutesToCalendarDateTime,
  calendarDateTimeToDate,
  compareCalendarDateTime,
  formatDateTimeBR,
  isoUtcToCalendarDateTime,
} from '../../../utils/date-utils';

const BRAZIL_TIMEZONE = 'America/Sao_Paulo';

export function filterAndSortActiveWindows(list: AvailabilityWindow[]): AvailabilityWindow[] {
  const now = new Date();
  return [...list]
    .filter((window) => {
      const endAtDate = window.endAt ? new Date(window.endAt) : null;
      return window.status === 'OPEN' && endAtDate && endAtDate > now;
    })
    .sort((a, b) => {
      const aDate = a.startAt ? new Date(a.startAt).getTime() : 0;
      const bDate = b.startAt ? new Date(b.startAt).getTime() : 0;
      return aDate - bDate;
    });
}

function isoToCalendarDateBR(iso: string): CalendarDate {
  const brazil = toZonedTime(new Date(iso), BRAZIL_TIMEZONE);
  return new CalendarDateClass(brazil.getFullYear(), brazil.getMonth() + 1, brazil.getDate());
}

export function availabilityWindowToCalendarDate(
  iso: string | undefined,
): CalendarDate | null {
  if (!iso) return null;
  return isoToCalendarDateBR(iso);
}

export function getWindowsOnDate(
  date: CalendarDate,
  windows: AvailabilityWindow[],
): AvailabilityWindow[] {
  return windows.filter((window) => isCalendarDateInAvailabilityWindow(date, window));
}

export type WindowRangePosition = 'single' | 'start' | 'middle' | 'end';

export function getWindowRangePositionOnDate(
  date: CalendarDate,
  window: Pick<AvailabilityWindow, 'startAt' | 'endAt'>,
): WindowRangePosition | null {
  if (!window.startAt || !window.endAt) return null;
  if (!isCalendarDateInAvailabilityWindow(date, window)) return null;

  const start = isoToCalendarDateBR(window.startAt);
  const end = isoToCalendarDateBR(window.endAt);

  if (start.compare(end) === 0) return 'single';
  if (date.compare(start) === 0) return 'start';
  if (date.compare(end) === 0) return 'end';
  return 'middle';
}

export function groupWindowsByStartDate(
  windows: AvailabilityWindow[],
): Array<{ date: CalendarDate; windows: AvailabilityWindow[] }> {
  const groups = new Map<string, { date: CalendarDate; windows: AvailabilityWindow[] }>();

  for (const window of windows) {
    if (!window.startAt) continue;
    const date = isoToCalendarDateBR(window.startAt);
    const key = `${date.year}-${date.month}-${date.day}`;
    const existing = groups.get(key);

    if (existing) {
      existing.windows.push(window);
      continue;
    }

    groups.set(key, { date, windows: [window] });
  }

  return [...groups.values()].sort((a, b) => {
    if (a.date.year !== b.date.year) return a.date.year - b.date.year;
    if (a.date.month !== b.date.month) return a.date.month - b.date.month;
    return a.date.day - b.date.day;
  });
}

export function isCalendarDateInAvailabilityWindow(
  date: CalendarDate,
  window: Pick<AvailabilityWindow, 'startAt' | 'endAt'>,
): boolean {
  if (!window.startAt || !window.endAt) return false;
  const start = isoToCalendarDateBR(window.startAt);
  const end = isoToCalendarDateBR(window.endAt);
  return date.compare(start) >= 0 && date.compare(end) <= 0;
}

export function hasAvailabilityWindowOnDate(
  date: CalendarDate,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
): boolean {
  return windows.some(
    (window) =>
      window.id !== excludeWindowId && isCalendarDateInAvailabilityWindow(date, window),
  );
}

const MIN_WINDOW_GAP_MINUTES = 1;

type BlockedInterval = { start: CalendarDateTime; end: CalendarDateTime };

function getDayBounds(date: CalendarDate): { start: CalendarDateTime; end: CalendarDateTime } {
  return {
    start: new CalendarDateTimeClass(date.year, date.month, date.day, 0, 0, 0),
    end: new CalendarDateTimeClass(date.year, date.month, date.day, 23, 59, 59),
  };
}

function clipWindowToDate(
  date: CalendarDate,
  window: Pick<AvailabilityWindow, 'startAt' | 'endAt'>,
): BlockedInterval | null {
  if (!window.startAt || !window.endAt) return null;

  const windowStart = isoUtcToCalendarDateTime(window.startAt);
  const windowEnd = isoUtcToCalendarDateTime(window.endAt);
  if (!windowStart || !windowEnd) return null;

  const { start: dayStart, end: dayEnd } = getDayBounds(date);
  if (compareCalendarDateTime(windowEnd, dayStart) < 0) return null;
  if (compareCalendarDateTime(windowStart, dayEnd) > 0) return null;

  const start =
    compareCalendarDateTime(windowStart, dayStart) > 0 ? windowStart : dayStart;
  const end = compareCalendarDateTime(windowEnd, dayEnd) < 0 ? windowEnd : dayEnd;
  return { start, end };
}

function mergeBlockedIntervals(intervals: BlockedInterval[]): BlockedInterval[] {
  if (intervals.length === 0) return [];

  const sorted = [...intervals].sort((a, b) => compareCalendarDateTime(a.start, b.start));
  const merged: BlockedInterval[] = [{ ...sorted[0]! }];

  for (let index = 1; index < sorted.length; index += 1) {
    const current = sorted[index]!;
    const last = merged[merged.length - 1]!;
    const contiguousStart = addMinutesToCalendarDateTime(last.end, MIN_WINDOW_GAP_MINUTES);

    if (compareCalendarDateTime(current.start, contiguousStart) <= 0) {
      if (compareCalendarDateTime(current.end, last.end) > 0) {
        last.end = current.end;
      }
      continue;
    }

    merged.push({ ...current });
  }

  return merged;
}

function getMergedBlockedIntervalsOnDate(
  date: CalendarDate,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
): BlockedInterval[] {
  const intervals = windows
    .filter((window) => window.id !== excludeWindowId)
    .map((window) => clipWindowToDate(date, window))
    .filter((interval): interval is BlockedInterval => interval !== null);

  return mergeBlockedIntervals(intervals);
}

function advancePastBlockedIntervals(
  cursor: CalendarDateTime,
  blocked: BlockedInterval[],
): CalendarDateTime {
  let next = cursor;

  for (const interval of blocked) {
    if (compareCalendarDateTime(interval.end, next) < 0) continue;
    if (compareCalendarDateTime(interval.start, addMinutesToCalendarDateTime(next, MIN_WINDOW_GAP_MINUTES)) > 0) {
      break;
    }
    if (compareCalendarDateTime(interval.end, next) >= 0) {
      next = interval.end;
    }
  }

  return next;
}

function hasMinimumFreeSlot(
  rangeStart: CalendarDateTime,
  rangeEnd: CalendarDateTime,
  blocked: BlockedInterval[],
): boolean {
  let cursor = rangeStart;

  for (const interval of blocked) {
    if (compareCalendarDateTime(interval.end, cursor) < 0) continue;
    if (compareCalendarDateTime(interval.start, rangeEnd) > 0) break;

    if (compareCalendarDateTime(interval.start, addMinutesToCalendarDateTime(cursor, MIN_WINDOW_GAP_MINUTES)) > 0) {
      return true;
    }

    if (compareCalendarDateTime(interval.end, cursor) >= 0) {
      cursor = interval.end;
    }
  }

  return compareCalendarDateTime(rangeEnd, addMinutesToCalendarDateTime(cursor, MIN_WINDOW_GAP_MINUTES)) >= 0;
}

/** Dia indisponível no calendário apenas quando não há nenhum intervalo livre de 1 minuto. */
export function isDateFullyBlockedByWindows(
  date: CalendarDate,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
  absoluteMin?: CalendarDateTime,
): boolean {
  const { start: dayStart, end: dayEnd } = getDayBounds(date);
  let rangeStart = dayStart;

  if (absoluteMin) {
    const absoluteDate = calendarDateTimeToDate(absoluteMin);
    if (date.compare(absoluteDate) < 0) return true;
    if (date.compare(absoluteDate) === 0 && compareCalendarDateTime(absoluteMin, rangeStart) > 0) {
      rangeStart = absoluteMin;
    }
  }

  if (compareCalendarDateTime(dayEnd, addMinutesToCalendarDateTime(rangeStart, MIN_WINDOW_GAP_MINUTES)) < 0) {
    return true;
  }

  const blocked = getMergedBlockedIntervalsOnDate(date, windows, excludeWindowId);
  return !hasMinimumFreeSlot(rangeStart, dayEnd, blocked);
}

export function getEarliestAllowedStartOnDate(
  date: CalendarDate,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
  absoluteMin?: CalendarDateTime,
): CalendarDateTime | null {
  const { start: dayStart, end: dayEnd } = getDayBounds(date);
  let cursor = dayStart;

  if (absoluteMin) {
    const absoluteDate = calendarDateTimeToDate(absoluteMin);
    if (date.compare(absoluteDate) < 0) return null;
    if (date.compare(absoluteDate) === 0 && compareCalendarDateTime(absoluteMin, cursor) > 0) {
      cursor = absoluteMin;
    }
  }

  const blocked = getMergedBlockedIntervalsOnDate(date, windows, excludeWindowId);
  cursor = advancePastBlockedIntervals(cursor, blocked);

  if (compareCalendarDateTime(dayEnd, addMinutesToCalendarDateTime(cursor, MIN_WINDOW_GAP_MINUTES)) < 0) {
    return null;
  }

  return cursor;
}

export function getAllowedEndBoundsOnDate(
  date: CalendarDate,
  startAt: CalendarDateTime,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
  absoluteMin?: CalendarDateTime,
): { min: CalendarDateTime; max: CalendarDateTime } | null {
  const { start: dayStart, end: dayEnd } = getDayBounds(date);
  const startDate = calendarDateTimeToDate(startAt);

  if (date.compare(startDate) < 0) return null;

  let minEnd =
    date.compare(startDate) === 0
      ? addMinutesToCalendarDateTime(startAt, MIN_WINDOW_GAP_MINUTES)
      : dayStart;

  if (absoluteMin && compareCalendarDateTime(absoluteMin, minEnd) > 0) {
    minEnd = absoluteMin;
  }

  const blocked = getMergedBlockedIntervalsOnDate(date, windows, excludeWindowId);
  minEnd = advancePastBlockedIntervals(minEnd, blocked);

  let maxEnd = dayEnd;
  for (const interval of blocked) {
    if (compareCalendarDateTime(interval.start, startAt) <= 0) continue;
    if (compareCalendarDateTime(interval.start, maxEnd) <= 0) {
      maxEnd = interval.start;
      break;
    }
  }

  if (compareCalendarDateTime(maxEnd, addMinutesToCalendarDateTime(minEnd, MIN_WINDOW_GAP_MINUTES - 1)) < 0) {
    return null;
  }

  return { min: minEnd, max: maxEnd };
}

/** Dia indisponível no calendário de fim quando não há intervalo válido após o início. */
export function isEndDateFullyBlockedByWindows(
  date: CalendarDate,
  startAt: CalendarDateTime,
  windows: AvailabilityWindow[],
  excludeWindowId?: string,
  absoluteMin?: CalendarDateTime,
): boolean {
  return getAllowedEndBoundsOnDate(date, startAt, windows, excludeWindowId, absoluteMin) === null;
}

export type DateTimeClampResult = {
  value: CalendarDateTime;
  wasClamped: boolean;
  reason?: 'before-window-end' | 'before-min' | 'after-max';
};

export function clampDateTimeToWindowBounds(
  dateTime: CalendarDateTime,
  mode: 'start' | 'end',
  windows: AvailabilityWindow[],
  options: {
    excludeWindowId?: string;
    absoluteMin?: CalendarDateTime;
    startAtReference?: CalendarDateTime | null;
  } = {},
): DateTimeClampResult {
  const date = calendarDateTimeToDate(dateTime);

  if (mode === 'start') {
    const minAllowed = getEarliestAllowedStartOnDate(
      date,
      windows,
      options.excludeWindowId,
      options.absoluteMin,
    );

    if (!minAllowed) return { value: dateTime, wasClamped: false };
    if (compareCalendarDateTime(dateTime, minAllowed) >= 0) {
      return { value: dateTime, wasClamped: false };
    }

    return {
      value: minAllowed,
      wasClamped: true,
      reason: 'before-window-end',
    };
  }

  if (!options.startAtReference) {
    return { value: dateTime, wasClamped: false };
  }

  const bounds = getAllowedEndBoundsOnDate(
    date,
    options.startAtReference,
    windows,
    options.excludeWindowId,
    options.absoluteMin,
  );

  if (!bounds) return { value: dateTime, wasClamped: false };

  if (compareCalendarDateTime(dateTime, bounds.min) < 0) {
    return {
      value: bounds.min,
      wasClamped: true,
      reason: 'before-min',
    };
  }

  if (compareCalendarDateTime(dateTime, bounds.max) > 0) {
    return {
      value: bounds.max,
      wasClamped: true,
      reason: 'after-max',
    };
  }

  return { value: dateTime, wasClamped: false };
}

/** Indicador no calendário: qualquer janela ativa, inclusive a em edição. */
export function hasAnyAvailabilityWindowOnDate(
  date: CalendarDate,
  windows: AvailabilityWindow[],
): boolean {
  return windows.some((window) => isCalendarDateInAvailabilityWindow(date, window));
}

export function periodsOverlap(
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean {
  return new Date(startA) < new Date(endB) && new Date(endA) > new Date(startB);
}

export function findOverlappingWindow(
  windows: AvailabilityWindow[],
  startAt: string,
  endAt: string,
  excludeWindowId?: string,
): AvailabilityWindow | undefined {
  return windows.find(
    (window) =>
      window.id !== excludeWindowId &&
      window.startAt &&
      window.endAt &&
      periodsOverlap(startAt, endAt, window.startAt, window.endAt),
  );
}

export function buildOverlapWindowErrorMessage(window: AvailabilityWindow): string {
  const existingPeriod = `${formatDateTimeBR(window.startAt)} até ${formatDateTimeBR(window.endAt)}`;
  return `Já existe uma janela de disponibilidade aberta neste período para esta vaga (período existente: ${existingPeriod})`;
}

const OVERLAP_WINDOW_ERROR_PREFIX = 'Já existe uma janela de disponibilidade aberta';
const OVERLAP_PERIOD_RE =
  /período existente:\s*(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2})\s*até\s*(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2})/;

export function isOverlapWindowError(message: string): boolean {
  return message.includes(OVERLAP_WINDOW_ERROR_PREFIX);
}

export function parseOverlapWindowPeriod(
  message: string,
): { start: string; end: string } | null {
  const match = message.match(OVERLAP_PERIOD_RE);
  if (!match) return null;
  return { start: match[1], end: match[2] };
}
